import React, { useState } from 'react';
import '../css/AddService.css';
import { create_holiday_request } from '../../api/holidayRequests';
import { useAuthProvider } from '../../providers/AuthProvider';
import { useAlert } from '../../providers/AlertProvider';

const RequestHolidayModal = ({ theme, isOpen, onClose, selectedDate, onSuccess }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [formData, setFormData] = useState({
        holidayType: '',
        notes: '',
    });

    const [errors, setErrors] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Helper function to format date as DD/MM/YYYY
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        let errorMessage = '';
    
        if (!formData.holidayType) {
            errorMessage = 'Please select a holiday type.';
        } else if (!selectedDate) {
            errorMessage = 'No date selected.';
        }
    
        setErrors(errorMessage);
        return !errorMessage;
    };

    const handleClose = () => {
        setFormData({
            holidayType: '',
            notes: '',
        });
        setErrors('');
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm() || !profile) return;

        setIsLoading(true);
        try {
            const formattedDate = new Date(selectedDate).toISOString().split('T')[0];
            const requestData = {
                profile_id: profile.id,
                request_date: formattedDate,
                request_reason: formData.holidayType,
                notes: formData.notes
            };

            const result = await create_holiday_request(requestData);

            if (result.success) {
                showAlert({
                    message: 'Holiday request submitted successfully! It is now pending approval by a manager.',
                    type: 'success',
                    buttons: [{ label: 'OK', type: 'success' }]
                });
                if (onSuccess) onSuccess();
                handleClose();
            } else {
                setErrors(result.error || 'Failed to submit request.');
            }
        } catch (error) {
            setErrors('An unexpected error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const isDark = theme === 'dark';
    const holidayTypes = [
        { key: 'annual_leave', label: 'Annual Leave' },
        { key: 'unpaid_leave', label: 'Unpaid Leave' },
        { key: 'other', label: 'Other / Medical' }
    ];

    return (
        <div id={theme} className="overlay" style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 9999 }}>
            <div className="modal-container">
                <h2>Request Holiday</h2>
                <form className="form" onSubmit={handleSubmit}>
                    
                    {/* Logged in User Display */}
                    <div className="input-group">
                        <label>Requesting For</label>
                        <div className="input" style={{ 
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5', 
                            border: isDark ? '1px solid #555' : '1px solid #ccc',
                            color: isDark ? '#fff' : '#333',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            opacity: 0.8
                        }}>
                            {profile ? `${profile.name} ${profile.surname}` : 'Stale Session'}
                        </div>
                    </div>

                    {/* Holiday Type Section */}
                    <div className="input-group">
                        <label>Holiday Type *</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '5px' }}>
                            {holidayTypes.map((type) => {
                                const isSelected = formData.holidayType === type.key;
                                return (
                                    <button
                                        key={type.key}
                                        type="button"
                                        style={{
                                            flex: '1 1 auto',
                                            padding: '8px 12px',
                                            borderRadius: '4px',
                                            border: isSelected 
                                                ? '2px solid #007bff' 
                                                : (isDark ? '1px solid #555' : '1px solid #ccc'),
                                            backgroundColor: isSelected 
                                                ? 'rgba(0, 123, 255, 0.25)' 
                                                : (isDark ? '#222' : '#fff'),
                                            color: isDark ? '#fff' : '#333',
                                            cursor: 'pointer',
                                            fontWeight: isSelected ? 'bold' : 'normal',
                                            fontSize: '13px'
                                        }}
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, holidayType: type.key }));
                                        }}
                                    >
                                        {type.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Selected Date Display */}
                    <div className="input-group">
                        <label>Requested Date</label>
                        <div className="input" style={{ 
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5', 
                            border: isDark ? '1px solid #555' : '1px solid #ccc',
                            color: isDark ? '#fff' : '#333',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 'bold'
                        }}>
                            {selectedDate ? formatDate(selectedDate) : 'No date selected'}
                        </div>
                        <small style={{ color: isDark ? '#ccc' : '#666', fontSize: '12px' }}>
                            Click on a different day in the calendar to change the date
                        </small>
                    </div>

                    {/* Notes */}
                    <div className="input-group">
                        <label>Notes (Optional)</label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            className="input"
                            rows="3"
                            placeholder="Enter request reason or additional notes..."
                        />
                    </div>

                    {errors && (
                        <div>
                            <span style={{ color: 'red' }}>{errors}</span>
                        </div>
                    )}
                </form>

                {/* Buttons */}
                <div className="button-group">
                    <button onClick={handleSubmit} className="submit-button" disabled={isLoading}>
                        {isLoading ? 'Submitting...' : 'Request Holiday'}
                    </button>
                    <button onClick={handleClose} className="close-button" disabled={isLoading}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RequestHolidayModal;
